import { RealtimeChannel } from '@supabase/supabase-js';
import { supabase } from './supabase';
import { ChatMessage } from '../types/chat';

type MessageHandler = (message: ChatMessage) => void;
type ResyncHandler = () => void;

interface Subscriber {
  onMessage: MessageHandler;
  onResync?: ResyncHandler;
}

/**
 * One realtime channel for the whole app, fanned out to every chat hook.
 *
 * Each delivery to a channel is billed as a realtime message, so the nav badge,
 * the conversation list, and the open thread all share a single subscription
 * instead of opening one each. RLS still decides which rows arrive.
 *
 * The channel is also dropped while the tab is hidden (after a grace period so
 * quick tab switches don't churn the socket), which keeps idle tabs out of the
 * peak-connection count. Hooks re-sync from the database on the way back.
 */

/** How long a hidden tab keeps its connection before releasing it. */
const HIDDEN_GRACE_MS = 60_000;

const subscribers = new Set<Subscriber>();
let channel: RealtimeChannel | null = null;
let opening = false;
let hideTimer: ReturnType<typeof setTimeout> | null = null;
let visibilityBound = false;

function tabHidden(): boolean {
  return typeof document !== 'undefined' && document.hidden;
}

function shouldBeOpen(): boolean {
  return subscribers.size > 0 && !tabHidden();
}

async function openChannel(): Promise<void> {
  if (channel || opening) return;
  opening = true;

  try {
    // Make sure the socket carries a fresh JWT before subscribing.
    const { data } = await supabase.auth.getSession();
    const token = data.session?.access_token;
    if (token) {
      await Promise.resolve((supabase.realtime as any).setAuth(token));
    }
  } catch {
    // supabase-js also syncs the token on auth state changes.
  }

  if (!shouldBeOpen()) {
    opening = false;
    return;
  }

  channel = supabase
    .channel('dm-messages')
    .on(
      'postgres_changes',
      { event: 'INSERT', schema: 'public', table: 'messages' },
      payload => {
        const message = payload.new as ChatMessage;
        subscribers.forEach(sub => sub.onMessage(message));
      }
    )
    .subscribe();

  opening = false;
}

function closeChannel(): void {
  if (!channel) return;
  supabase.removeChannel(channel);
  channel = null;
}

function sync(): void {
  if (shouldBeOpen()) {
    openChannel();
  } else {
    closeChannel();
  }
}

function handleVisibilityChange(): void {
  if (hideTimer) {
    clearTimeout(hideTimer);
    hideTimer = null;
  }

  if (tabHidden()) {
    hideTimer = setTimeout(() => {
      hideTimer = null;
      closeChannel();
    }, HIDDEN_GRACE_MS);
    return;
  }

  const missedMessages = !channel;
  sync();
  if (missedMessages) {
    subscribers.forEach(sub => sub.onResync?.());
  }
}

/**
 * Registers a listener for incoming messages. `onResync` fires when the tab
 * comes back after the connection was dropped, so state can be refetched.
 * Returns an unsubscribe function.
 */
export function subscribeToMessages(
  onMessage: MessageHandler,
  onResync?: ResyncHandler
): () => void {
  const subscriber: Subscriber = { onMessage, onResync };
  subscribers.add(subscriber);

  if (typeof document !== 'undefined' && !visibilityBound) {
    document.addEventListener('visibilitychange', handleVisibilityChange);
    visibilityBound = true;
  }

  sync();

  return () => {
    subscribers.delete(subscriber);
    if (subscribers.size > 0) return;

    if (hideTimer) {
      clearTimeout(hideTimer);
      hideTimer = null;
    }
    closeChannel();
    if (visibilityBound) {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      visibilityBound = false;
    }
  };
}
